import { cn } from "@babybjorn/ui";
import type { ChangeEvent, FC, ReactNode } from "react";
import type { ActiveSearchType } from "../../../../context/app/reducer";
import Divider from "../../../general/Divider";
import List from "../../../general/List";
import ListItem from "../../../general/List/components/ListItem/ListItem";
import ToggleButton from "../../../general/ToggleButton";
import QuickSearchModal from "../QuckSearchModal/QuickSearchModal";
import * as s from "./QuickSearch.module.scss";

type QuickSearchProps = {
	searchType: "preSearch" | "postSearch";
	searchList: ReactNode;
	products: ReactNode;
	pages: ReactNode;
	searchValue: string;
	setSearchValue: (event: ChangeEvent<HTMLInputElement>) => void;
	isSearchOpen: boolean;
	labels: Record<
		| "getHelpLabel"
		| "customerServiceLabel"
		| "faqLabel"
		| "shippingLabel"
		| "suggestionsLabel"
		| "popularLabel"
		| "productsLabel"
		| "trendingLabel"
		| "pagesLabel"
		| "showAllLabel",
		string
	>;
	activeOption: "product" | "page";
	closeSearch: () => void;
	resetUiHandler: () => void;
	setActiveSearchType: (selectedOption: ActiveSearchType) => void;
	getHelpLinks: { [key: string]: { target: string; url: string } };
};

const QuickSearch: FC<QuickSearchProps> = ({
	searchType,
	searchList,
	products,
	pages,
	searchValue,
	activeOption,
	setSearchValue,
	isSearchOpen,
	labels,
	closeSearch,
	resetUiHandler,
	setActiveSearchType,
	getHelpLinks,
}) => {
	const { getHelpLabel, customerServiceLabel, faqLabel, shippingLabel } =
		labels;

	return (
		<QuickSearchModal
			isOpen={isSearchOpen}
			closeSearch={closeSearch}
			clearSearch={resetUiHandler}
			searchValue={searchValue}
			setSearchValue={setSearchValue}
			labels={labels}
		>
			{searchList}
			<Divider variant="responsive" />
			{searchType === "postSearch" && (
				<div className={s.toggleWrapper}>
					<ToggleButton
						toggleLabels={["Products", "Pages"]}
						active={activeOption === "product" ? 0 : 1}
						alwaysSmall
						onToggle={(selectedOption) =>
							setActiveSearchType(selectedOption === 0 ? "product" : "page")
						}
					/>
				</div>
			)}
			{activeOption === "product" && products}
			{activeOption === "page" && pages}
			<Divider
				variant="responsive"
				className={cn({ [s.desktop]: searchType === "postSearch" })}
			/>

			{searchType === "preSearch" ? (
				<List search>
					<h4>{getHelpLabel}</h4>
					<ListItem
						variant="list"
						title={customerServiceLabel}
						href={getHelpLinks.contactUs?.url}
						target={getHelpLinks.contactUs?.target}
					/>
					<ListItem
						variant="list"
						title={faqLabel}
						href={getHelpLinks.faq?.url}
						target={getHelpLinks.faq?.target}
					/>
					<ListItem
						variant="list"
						title={shippingLabel}
						href={getHelpLinks.shipping?.url}
						target={getHelpLinks.shipping?.target}
					/>
				</List>
			) : (
				<div className={cn(s.desktop, s.pages)}>{pages}</div>
			)}
		</QuickSearchModal>
	);
};

export default QuickSearch;
